import { useEffect, useState } from 'react'
import { KTIcon } from '../../../helpers'
import { useParams } from 'react-router-dom'
import { getRequest } from '../../../../app/pages/Helpers/Requests'
import PaginatedItems from '../../../../app/UI/PaginatedItems'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import DownloadOcpiSessionsModal from '../DownloadOcpiSessionsModal'

export default function OcpiSessions() {
  let { pointId } = useParams()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [sessions, setSessions] = useState<any[]>([])
  const [page, setPage] = useState<number>(1)
  const [limit] = useState<number>(10)
  const [pages, setPages] = useState<number>(1)
  const [downloading, setDownloading] = useState(false)

  const convertJsonToExcel = async (json: any) => {
    const ws = XLSX.utils.json_to_sheet(json)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const data = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    })
    saveAs(data, `session${new Date().toDateString()}.xlsx`)
  }

  const fetchData = async () => {
    try {
      const response = await getRequest(`/ocpi/msp/${pointId}/sessions?page=${page}&limit=${limit}`)
      if (response && response.data) {
        console.log('Fetched sessions:', response.data)
        setSessions(response.data.data)
        setPages(response.data.pagination?.totalPages || 1)
      }
    } catch (error) {
      console.error('Error fetching sessions:', error)
    }
  }

  const downloadSessions = async () => {
    try {
      setDownloading(true)
      const res = await getRequest(`/ocpi/msp/${pointId}/sessions/export?startDate=2024-01-01&endDate=2025-12-31`)
      if (res) {
        console.log('Downloaded sessions:', res.data.data)
        await convertJsonToExcel(res.data.data)
      }
    } catch (error) {
      console.error('Error downloading sessions:', error)
    } finally {
      setDownloading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [page])

  // Format date for display
  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    if (amount === null || amount === undefined) return 'N/A'
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  // Render charging periods
  const renderChargingPeriods = (periods: any[]) => {
    if (!periods || periods.length === 0) return 'No charging periods'
    
    return (
      <div className='table-responsive'>
        <table className='table table-sm table-bordered table-striped align-middle gs-0 gy-3'>
          <thead className='table-light'>
            <tr className='fw-bold text-muted bg-light'>
              <th className='min-w-150px ps-4 pe-4 py-3 text-uppercase'>START TIME</th>
              <th className='min-w-120px ps-4 pe-4 py-3 text-uppercase'>DIMENSION TYPE</th>
              <th className='min-w-100px ps-4 pe-4 py-3 text-uppercase'>VOLUME</th>
              <th className='min-w-100px ps-4 pe-4 py-3 text-uppercase'>TARIFF ID</th>
            </tr>
          </thead>
          <tbody>
            {periods.map((period, index) => (
              <tr key={index} className='border-bottom border-gray-200'>
                <td className='ps-4 pe-4 py-3'>{formatDateTime(period.start_date_time)}</td>
                <td className='ps-4 pe-4 py-3'>
                  {period.dimensions?.map((dim: any, dimIndex: number) => (
                    <div key={dimIndex} className='mb-1'>
                      <span className='badge badge-light-primary fs-7 fw-bold'>{dim.type}</span>
                    </div>
                  ))}
                </td>
                <td className='ps-4 pe-4 py-3'>
                  {period.dimensions?.map((dim: any, dimIndex: number) => (
                    <div key={dimIndex} className='mb-1'>
                      <span className='fw-bold text-primary'>{dim.volume} kWh</span>
                    </div>
                  ))}
                </td>
                <td className='ps-4 pe-4 py-3'>
                  <span className='badge badge-light-info fs-7 fw-bold'>{period.tariff_id || 'N/A'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Render CDR token details
  const renderCdrToken = (token: any) => {
    if (!token) return 'No token information'
    
    return (
      <div className='table-responsive'>
        <table className='table table-sm table-bordered table-striped align-middle gs-0 gy-2'>
          <tbody>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-120px'>COUNTRY CODE</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='badge badge-light-success fs-7 fw-bold'>{token.country_code || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-120px'>PARTY ID</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-dark'>{token.party_id || 'N/A'}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-120px'>UID</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-primary'>{token.uid || 'N/A'}</span>
              </td>
            </tr>
            <tr>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-120px'>TYPE</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='badge badge-light-info fs-7 fw-bold'>{token.type || 'N/A'}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  // Render total cost details
  const renderTotalCost = (cost: any) => {
    if (!cost) return 'No cost information'
    
    return (
      <div className='table-responsive'>
        <table className='table table-sm table-bordered table-striped align-middle gs-0 gy-2'>
          <tbody>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>EXCLUDING VAT</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-success fs-6'>{formatCurrency(cost.excl_vat, cost.currency)}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>INCLUDING VAT</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-primary fs-6'>{formatCurrency(cost.incl_vat, cost.currency)}</span>
              </td>
            </tr>
            <tr className='border-bottom border-gray-200'>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>VAT AMOUNT</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='fw-bold text-warning fs-6'>{formatCurrency(cost.vat, cost.currency)}</span>
              </td>
            </tr>
            <tr>
              <td className='fw-bold text-uppercase ps-4 pe-4 py-3 min-w-140px'>CURRENCY</td>
              <td className='ps-4 pe-4 py-3'>
                <span className='badge badge-light-success fs-7 fw-bold'>{cost.currency || 'N/A'}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  // Render session details
  const renderSessionDetails = (session: any) => {
    return (
      <div className='p-6 bg-light rounded'>
        <div className='row g-5'>
          {/* Basic Information */}
          <div className='col-md-6'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-primary text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>BASIC INFORMATION</h6>
              </div>
              <div className='card-body p-4'>
                <div className='table-responsive'>
                  <table className='table table-sm table-borderless align-middle gs-0 gy-2'>
                    <tbody>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>COUNTRY CODE</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='badge badge-light-success fs-7 fw-bold'>{session.country_code || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>PARTY ID</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='fw-bold text-dark'>{session.party_id || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>SESSION ID</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='badge badge-light-primary fs-7 fw-bold'>{session.id || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>AUTH METHOD</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='badge badge-light-info fs-7 fw-bold'>{session.auth_method || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>AUTH REFERENCE</td>
                        <td className='ps-0 pe-0 py-3'>
                          <small className='text-muted fw-semibold'>{session.authorization_reference || 'N/A'}</small>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className='col-md-6'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-success text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>LOCATION & EQUIPMENT</h6>
              </div>
              <div className='card-body p-4'>
                <div className='table-responsive'>
                  <table className='table table-sm table-borderless align-middle gs-0 gy-2'>
                    <tbody>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>LOCATION ID</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='fw-bold text-dark'>{session.location_id || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>EVSE UID</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='fw-bold text-primary'>{session.evse_uid || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>CONNECTOR ID</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='fw-bold text-info'>{session.connector_id || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr className='border-bottom border-gray-200'>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>METER ID</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='fw-bold text-warning'>{session.meter_id || 'N/A'}</span>
                        </td>
                      </tr>
                      <tr>
                        <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>CURRENCY</td>
                        <td className='ps-0 pe-0 py-3'>
                          <span className='badge badge-light-success fs-7 fw-bold'>{session.currency || 'N/A'}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* CDR Token */}
          <div className='col-md-6'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-warning text-dark py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-dark'>CDR TOKEN</h6>
              </div>
              <div className='card-body p-4'>
                {renderCdrToken(session.cdr_token)}
              </div>
            </div>
          </div>

          {/* Total Cost */}
          <div className='col-md-6'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-info text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>COST BREAKDOWN</h6>
              </div>
              <div className='card-body p-4'>
                {renderTotalCost(session.total_cost)}
              </div>
            </div>
          </div>

          {/* Charging Periods */}
          <div className='col-12'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-secondary text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>CHARGING PERIODS</h6>
              </div>
              <div className='card-body p-4'>
                {renderChargingPeriods(session.charging_periods)}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className='col-12'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-dark text-white py-3'>
                <h6 className='card-title mb-0 fw-bold text-uppercase fs-6 text-white'>ADDITIONAL INFORMATION</h6>
              </div>
              <div className='card-body p-4'>
                <div className='row g-4'>
                  <div className='col-md-6'>
                    <div className='table-responsive'>
                      <table className='table table-sm table-borderless align-middle gs-0 gy-2'>
                        <tbody>
                          <tr className='border-bottom border-gray-200'>
                            <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>START DATE TIME</td>
                            <td className='ps-0 pe-0 py-3'>
                              <span className='fw-bold text-dark'>{formatDateTime(session.start_date_time)}</span>
                            </td>
                          </tr>
                          <tr className='border-bottom border-gray-200'>
                            <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>END DATE TIME</td>
                            <td className='ps-0 pe-0 py-3'>
                              <span className='fw-bold text-dark'>{formatDateTime(session.end_date_time)}</span>
                            </td>
                          </tr>
                          <tr>
                            <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>ENERGY (KWH)</td>
                            <td className='ps-0 pe-0 py-3'>
                              <span className='badge badge-light-primary fs-7 fw-bold'>{session.kwh || 'N/A'} kWh</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='table-responsive'>
                      <table className='table table-sm table-borderless align-middle gs-0 gy-2'>
                        <tbody>
                          <tr className='border-bottom border-gray-200'>
                            <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>STATUS</td>
                            <td className='ps-0 pe-0 py-3'>
                              <span className={`badge fs-7 fw-bold ${
                                session.status === 'COMPLETED' ? 'badge-light-success' :
                                session.status === 'ACTIVE' ? 'badge-light-warning' :
                                'badge-light-danger'
                              }`}>
                                {session.status || 'N/A'}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td className='fw-bold text-uppercase ps-0 pe-4 py-3 min-w-140px'>LAST UPDATED</td>
                            <td className='ps-0 pe-0 py-3'>
                              <span className='fw-bold text-dark'>{formatDateTime(session.last_updated)}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>OCPI SESSIONS</span>
          <span className='text-muted mt-1 fw-semibold fs-7'>Manage and view charging session data</span>
        </h3>
        <div className='card-toolbar'>
          <button
            type='button'
            className='btn btn-sm btn-light-primary'
            data-bs-toggle='modal'
            data-bs-target='#downloadSessionsModal'
          >
            <KTIcon iconName='download' className='fs-2' />
            Download Sessions
          </button>
          <DownloadOcpiSessionsModal />
        </div>
      </div>
      
      <div className='card-body py-4'>
        <div className='table-responsive'>
          <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className='min-w-200px ps-6 pe-6 py-4'>SESSION ID</th>
                <th className='min-w-150px ps-6 pe-6 py-4'>START TIME</th>
                <th className='min-w-150px ps-6 pe-6 py-4'>END TIME</th>
                <th className='min-w-100px ps-6 pe-6 py-4'>ENERGY (KWH)</th>
                <th className='min-w-120px ps-6 pe-6 py-4'>STATUS</th>
                <th className='min-w-100px ps-6 pe-6 py-4'>COST</th>
                <th className='min-w-100px text-end ps-6 pe-6 py-4'>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <>
                  <tr key={session.id} className='border-bottom border-gray-200'>
                    <td className='ps-6 pe-6 py-4'>
                      <div className='d-flex align-items-center'>
                        {/* <div className='symbol symbol-50px me-5'>
                          <span className='symbol-label bg-light-primary text-primary fw-bold fs-7'>
                            {session.id?.substring(0, 8) || 'N/A'}
                          </span>
                        </div> */}
                        <div className='d-flex justify-content-start flex-column'>
                          <span className='text-dark fw-bold text-hover-primary mb-1 fs-6'>
                            {session.id || 'N/A'}
                          </span>
                          <span className='text-muted fw-semibold text-muted d-block fs-7'>
                            {session.party_id} - {session.country_code}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className='ps-6 pe-6 py-4'>
                      <span className='text-dark fw-bold text-hover-primary d-block fs-6'>
                        {formatDateTime(session.start_date_time)}
                      </span>
                    </td>
                    <td className='ps-6 pe-6 py-4'>
                      <span className='text-dark fw-bold text-hover-primary d-block fs-6'>
                        {formatDateTime(session.end_date_time)}
                      </span>
                    </td>
                    <td className='ps-6 pe-6 py-4'>
                      <span className='badge badge-light-primary fs-7 fw-bold'>
                        {session.kwh || 'N/A'} kWh
                      </span>
                    </td>
                    <td className='ps-6 pe-6 py-4'>
                      <span className={`badge fs-7 fw-bold ${
                        session.status === 'COMPLETED' ? 'badge-light-success' :
                        session.status === 'ACTIVE' ? 'badge-light-warning' :
                        'badge-light-danger'
                      }`}>
                        {session.status || 'N/A'}
                      </span>
                    </td>
                    <td className='ps-6 pe-6 py-4'>
                      <span className='text-dark fw-bold text-hover-primary d-block fs-6'>
                        {session.total_cost ? formatCurrency(session.total_cost.incl_vat, session.total_cost.currency) : 'N/A'}
                      </span>
                    </td>
                    <td className='text-end ps-6 pe-6 py-4'>
                      <button
                        className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-2'
                        onClick={() => setExpandedId(expandedId === session.id ? null : session.id)}
                        title={expandedId === session.id ? 'Hide Details' : 'View Details'}
                      >
                        <KTIcon iconName={expandedId === session.id ? 'eye-slash' : 'eye'} className='fs-3' />
                      </button>
                    </td>
                  </tr>

                  {/* Expanded details row */}
                  {expandedId === session.id && (
                    <tr>
                      <td colSpan={7} className='p-0'>
                        {renderSessionDetails(session)}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination */}
      <div className='card-footer'>
        <PaginatedItems setPage={setPage} pages={pages} currentPage={page} />
      </div>
    </div>
  )
}
